import {
	ApiError,
	type OrderCatalogSku,
	type PackagingOption,
	type PortalCity,
	type PortalOrder,
	type PortalOrderIntakeResult,
} from "@khazzin/api-client";
import { formatMoney } from "@khazzin/i18n/money";
import {
	collectedOnSite,
	OrderServiceFields,
	OrderTypeCards,
	usesStock,
} from "@khazzin/ui/patterns";
import {
	Form,
	FormErrorSummary,
	FormField,
	FormSection,
	MoneyInput,
	useApiForm,
} from "@khazzin/ui/patterns/form";
import { ErrorState, NotFoundState, PageHeader } from "@khazzin/ui/patterns/page";
import {
	Button,
	Checkbox,
	Combobox,
	Input,
	Select,
	SkeletonText,
	Switch,
} from "@khazzin/ui/primitives";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";
import {
	addSku,
	type DraftValues,
	draftOf,
	emptyDraft,
	loadDraft,
	previewTotals,
	STORE_DEFAULT,
	saveDraft,
	toPatch,
	toPortalDraft,
	validateDraft,
} from "./draft";

/**
 * The Store Portal's New Order form (EP10-S05, DA-10-04): Customer, the four
 * type boxes, Delivery with the City that fills its Province, Items with the
 * SKU picker and the per-item or combined package, notes, and a sticky footer
 * with the totals and Submit.
 *
 * **Three modes, one form.** New (`/new-order`) creates; an order that is
 * still `pending` opens at `/orders/:id/edit` to correct its customer,
 * delivery and payment; an order withdrawn with the circle opens at the same
 * address with its items editable and **Resubmit**.
 *
 * **One idempotency key per opening**, so a double tap on Submit replays the
 * first order instead of numbering a second. A refusal the store can fix gets
 * a fresh key, because the next attempt is a different request.
 *
 * **An unsent new order survives a reload**: it is kept per store in
 * `sessionStorage` until it is submitted.
 */

type Mode = "new" | "edit" | "resubmit";

function mintKey(): string {
	return `por-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** `/new-order`: a new order for the active store. */
export function NewOrderPage() {
	const { t } = useLocale();
	const { store } = useStoreScope();
	if (levelOf(store, "new_order") !== "write") {
		return <NotFound />;
	}
	return (
		<div className="flex flex-col gap-4">
			<PageHeader title={t("portal.newOrder.title")} subtitle={t("portal.newOrder.subtitle")} />
			<OrderForm key={store?.storeId} mode="new" storeId={store?.storeId ?? ""} />
		</div>
	);
}

/** `/orders/:orderId/edit`: a pending order to correct, or a withdrawn one to resubmit. */
export function EditOrderPage() {
	const { t } = useLocale();
	const api = useApi();
	const { orderId = "" } = useParams();
	const { store } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const writable = levelOf(store, "orders") === "write";
	const detail = useQuery({
		queryKey: ["portal", storeId, "orders", "detail", orderId],
		queryFn: ({ signal }) => api.getPortalOrder(orderId, { signal }),
		enabled: writable && storeId !== "",
		retry: false,
	});

	if (!writable) {
		return <NotFound />;
	}
	if (detail.error instanceof ApiError && detail.error.status === 404) {
		return <NotFoundState t={t} />;
	}
	if (detail.isError) {
		return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} t={t} />;
	}
	if (detail.data === undefined) {
		return <SkeletonText lines={6} />;
	}
	const order = detail.data;
	const mode: Mode | null = order.withdrawn
		? "resubmit"
		: order.order.status === "pending"
			? "edit"
			: null;
	const back = `/orders/${encodeURIComponent(order.order.orderId)}`;
	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t(
					mode === "resubmit" ? "portal.newOrder.resubmitTitle" : "portal.newOrder.editTitle",
					{
						orderNo: order.order.orderNo,
					},
				)}
				backTo={back}
				backLabel={order.order.orderNo}
			/>
			{mode === null ? (
				<p role="status" className="text-[13px] text-fg-muted">
					{t("portal.newOrder.locked")}
				</p>
			) : (
				<OrderForm
					key={`${order.order.orderId}-${mode}`}
					mode={mode}
					storeId={storeId}
					order={order}
				/>
			)}
		</div>
	);
}

function OrderForm({ mode, storeId, order }: { mode: Mode; storeId: string; order?: PortalOrder }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const navigate = useNavigate();
	const idempotencyKey = useRef(mintKey());
	const skus = useRef(new Map<string, OrderCatalogSku>());
	const cities = useRef(new Map<string, PortalCity>());
	const [done, setDone] = useState<PortalOrderIntakeResult | null>(null);
	const itemsEditable = mode !== "edit";

	const packagingQuery = useQuery({
		queryKey: ["portal", storeId, "packaging"],
		queryFn: ({ signal }) => api.getPortalPackagingTypes({ signal }),
		enabled: storeId !== "" && itemsEditable,
	});
	// ADJ-1022-3: the store's default service and the pickup warehouses.
	const optionsQuery = useQuery({
		queryKey: ["portal", storeId, "orderOptions"],
		queryFn: ({ signal }) => api.getPortalOrderOptions({ signal }),
		enabled: storeId !== "" && itemsEditable,
	});
	const packaging: PackagingOption[] = packagingQuery.data?.items ?? [];
	// ADJ-M03-4: a store with no package types offered yet packs with the
	// warehouse's default — no pickers with nothing in them, and no combine
	// switch that would demand a package nobody can choose.
	const noPackaging = packagingQuery.isSuccess && packaging.length === 0;
	const currency = order?.order.currency ?? packaging[0]?.currency ?? "USD";

	const saved = order === undefined ? loadDraft(storeId) : null;
	const initial = order === undefined ? (saved ?? emptyDraft()) : draftOf(order);
	const form = useApiForm<DraftValues>({
		initial,
		validate: (values) => validateDraft(values, t, itemsEditable),
		submit: async (values) => {
			try {
				if (mode === "edit" && order !== undefined) {
					await api.editPortalOrder(order.order.orderId, toPatch(values), order.version);
					await queries.invalidateQueries({ queryKey: ["portal", storeId, "orders"] });
					void navigate(`/orders/${encodeURIComponent(order.order.orderId)}`);
					return;
				}
				const result =
					mode === "resubmit" && order !== undefined
						? await api.resubmitPortalOrder(
								order.order.orderId,
								toPortalDraft(values),
								idempotencyKey.current,
							)
						: await api.createPortalOrder(toPortalDraft(values), idempotencyKey.current);
				if (mode === "new") {
					saveDraft(storeId, null);
				}
				await queries.invalidateQueries({ queryKey: ["portal", storeId, "orders"] });
				setDone(result);
			} catch (error) {
				if (error instanceof ApiError && error.status < 500) {
					idempotencyKey.current = mintKey();
				}
				throw error;
			}
		},
	});
	const values = form.values;

	// The unsent new order, kept per store until it is submitted.
	useEffect(() => {
		if (mode === "new" && done === null) {
			saveDraft(storeId, values);
		}
	}, [mode, storeId, values, done]);

	// A new order opens on the store's default service (ADJ-1022-3), once it
	// is known; a kept draft keeps the one it was left with.
	const { set } = form;
	const defaultMode = optionsQuery.data?.defaultServiceMode;
	const cardsRef = useRef(values.orderCards);
	cardsRef.current = values.orderCards;
	const fresh = mode === "new" && saved === null;
	useEffect(() => {
		if (fresh && defaultMode !== undefined) {
			set("orderCards", { ...cardsRef.current, serviceMode: defaultMode });
		}
	}, [fresh, defaultMode, set]);

	// A draft kept from before the offer changed may still say "combine".
	useEffect(() => {
		if (noPackaging && values.combine) {
			set("combine", false);
		}
	}, [noPackaging, values.combine, set]);

	if (done !== null) {
		return (
			<Confirmation
				result={done}
				preview={previewTotals(values, packaging)}
				onAnother={() => {
					idempotencyKey.current = mintKey();
					form.reset(emptyDraft());
					setDone(null);
				}}
			/>
		);
	}

	const totals = previewTotals(values, packaging);
	const collected = collectedOnSite(values.orderCards);
	const packed = usesStock(values.orderCards);
	const warehouses = optionsQuery.data?.pickupWarehouses ?? [];
	const money = (amountMinor: number) => formatMoney({ amountMinor, currency }, locale);
	const packageOptions = packaging.map((option) => ({
		value: option.packageTypeId,
		label: `${locale === "ar" && option.nameAr !== "" ? option.nameAr : option.name} · ${money(option.priceMinor)}`,
	}));
	const setLine = (rowId: string, patch: Partial<DraftValues["lines"][number]>) =>
		form.set(
			"lines",
			values.lines.map((line) => (line.rowId === rowId ? { ...line, ...patch } : line)),
		);

	return (
		<Form form={form} aria-label={t("portal.newOrder.title")} className="max-w-3xl pb-2">
			<FormErrorSummary form={form} t={t} />

			<FormSection title={t("portal.newOrder.sections.customer")} columns={2}>
				<FormField name="customerName" label={t("portal.newOrder.fields.name")} optional>
					{(control) => (
						<Input
							{...control}
							value={values.customerName}
							onChange={(event) => form.set("customerName", event.target.value)}
						/>
					)}
				</FormField>
				<FormField
					name="customerPhone"
					label={t("portal.newOrder.fields.phone")}
					hint={t("portal.newOrder.fields.phoneHint")}
					required
				>
					{(control) => (
						<Input
							{...control}
							type="tel"
							inputMode="tel"
							dir="ltr"
							value={values.customerPhone}
							onChange={(event) => form.set("customerPhone", event.target.value)}
						/>
					)}
				</FormField>
			</FormSection>

			<FormSection title={t("portal.newOrder.sections.type")} columns={2}>
				<Checkbox
					label={t("portal.newOrder.type.cod")}
					checked={values.cod}
					onCheckedChange={(checked) => form.set("cod", checked === true)}
				/>
				<Checkbox
					label={t("portal.newOrder.type.fragile")}
					checked={values.fragile}
					onCheckedChange={(checked) => form.set("fragile", checked === true)}
				/>
				<Checkbox
					label={t("portal.newOrder.type.urgent")}
					checked={values.urgent}
					onCheckedChange={(checked) => form.set("urgent", checked === true)}
				/>
				{values.cod && (
					<FormField
						name="codAmountMinor"
						label={t("portal.newOrder.fields.cod")}
						hint={t("portal.newOrder.fields.codHint")}
						required
					>
						{(control) => (
							<MoneyInput
								{...control}
								currency={currency}
								min={0}
								value={values.codAmountMinor}
								onChange={(next) => form.set("codAmountMinor", next)}
							/>
						)}
					</FormField>
				)}
			</FormSection>

			<OrderServiceFields
				value={values.orderCards}
				onChange={(next) => form.set("orderCards", next)}
				t={t}
				// The order's service and type are fixed once it exists (`07/01` §3.6).
				disabled={mode === "edit"}
			/>
			<OrderTypeCards
				value={values.orderCards}
				onChange={(next) => form.set("orderCards", next)}
				t={t}
				currency={currency}
				disabled={mode === "edit"}
				renderWarehouse={(control) => (
					<Select
						{...control}
						value={values.orderCards.pickupWarehouseId}
						disabled={mode === "edit"}
						placeholder={t("orderCards.fields.warehousePlaceholder")}
						emptyLabel={t("orderCards.fields.warehouseEmpty")}
						onValueChange={(next) =>
							form.set("orderCards", { ...values.orderCards, pickupWarehouseId: next })
						}
						options={warehouses.map((site) => ({
							value: site.warehouseId,
							label: site.customerPickup
								? t("orderCards.fields.warehousePickupSite", { name: site.name })
								: site.name,
						}))}
					/>
				)}
				searchSkus={async (query, signal) =>
					(await api.searchPortalCatalogSkus({ q: query, signal })).items.map((row) => ({
						sku: row.skuCode,
						name: row.name,
					}))
				}
			/>

			<FormSection title={t("portal.newOrder.sections.delivery")} columns={2}>
				{!collected && (
					<FormField name="cityId" label={t("portal.newOrder.fields.city")} required>
						{(control) => (
							<Combobox
								{...control}
								value={values.cityId === "" ? null : values.cityId}
								selectedLabel={values.cityName}
								placeholder={t("portal.newOrder.fields.cityPlaceholder")}
								emptyLabel={t("portal.newOrder.fields.cityEmpty")}
								loadOptions={async (query, signal) => {
									const page = await api.searchPortalCities(query, { signal });
									cities.current = new Map(page.items.map((city) => [city.cityId, city]));
									return page.items.map((city) => ({
										value: city.cityId,
										label: locale === "ar" && city.nameAr !== "" ? city.nameAr : city.name,
										hint: city.provinceName,
									}));
								}}
								onValueChange={(cityId) => {
									const city = cityId === null ? undefined : cities.current.get(cityId);
									form.set("cityId", city?.cityId ?? "");
									// In the reader's language, as the picker showed it (ADJ-M03-4).
									form.set(
										"cityName",
										city === undefined
											? ""
											: locale === "ar" && city.nameAr !== ""
												? city.nameAr
												: city.name,
									);
									form.set("provinceId", city?.provinceId ?? "");
									form.set("provinceName", city?.provinceName ?? "");
								}}
							/>
						)}
					</FormField>
				)}
				{!collected && (
					<FormField
						name="provinceId"
						label={t("portal.newOrder.fields.province")}
						hint={t("portal.newOrder.fields.provinceHint")}
					>
						{(control) => <Input {...control} value={values.provinceName} readOnly />}
					</FormField>
				)}
				{!collected && (
					<FormField
						name="addressLine"
						label={t("portal.newOrder.fields.address")}
						hint={t("portal.newOrder.fields.addressHint")}
						required
					>
						{(control) => (
							<Input
								{...control}
								value={values.addressLine}
								onChange={(event) => form.set("addressLine", event.target.value)}
							/>
						)}
					</FormField>
				)}
				<FormField
					name="notes"
					label={t("portal.newOrder.fields.notes")}
					hint={t("portal.newOrder.fields.notesHint")}
					optional
				>
					{(control) => (
						<Input
							{...control}
							value={values.notes}
							onChange={(event) => form.set("notes", event.target.value)}
						/>
					)}
				</FormField>
			</FormSection>

			<FormSection
				title={t("portal.newOrder.sections.items")}
				{...(packed ? {} : { description: t("orderCards.lines.reference") })}
			>
				{itemsEditable ? (
					<FormField name="lines" label={t("portal.newOrder.fields.sku")}>
						{(control) => (
							<Combobox
								{...control}
								value={null}
								placeholder={t("portal.newOrder.fields.skuPlaceholder")}
								emptyLabel={t("portal.newOrder.fields.skuEmpty")}
								loadOptions={async (query, signal) => {
									const page = await api.searchPortalCatalogSkus({ q: query, signal });
									skus.current = new Map(page.items.map((row) => [row.skuId, row]));
									return page.items.map((row) => ({
										value: row.skuId,
										label: row.name,
										hint: [
											row.skuCode,
											row.available === null || row.available === undefined
												? null
												: t("portal.newOrder.fields.available", { available: row.available }),
											row.retailPriceMinor === null || row.retailPriceMinor === undefined
												? null
												: money(row.retailPriceMinor),
										]
											.filter((part) => part !== null)
											.join(" · "),
									}));
								}}
								onValueChange={(skuId) => {
									const row = skuId === null ? undefined : skus.current.get(skuId);
									if (row !== undefined) {
										form.set("lines", addSku(values.lines, row));
									}
								}}
							/>
						)}
					</FormField>
				) : (
					<p className="text-[12.5px] text-fg-muted">{t("portal.newOrder.itemsLocked")}</p>
				)}
				<ul className="flex flex-col gap-2">
					{values.lines.map((line, index) => (
						<li
							key={line.rowId}
							className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-end"
						>
							<div className="min-w-0 flex-1">
								<p className="truncate text-[13px] font-bold text-fg">{line.name}</p>
								<p className="font-mono text-[11.5px] text-fg-muted">{line.sku}</p>
							</div>
							<FormField
								name={`lines[${index}].qty`}
								label={t("portal.newOrder.lines.qty")}
								className="sm:w-24"
							>
								{(control) => (
									<Input
										{...control}
										type="number"
										min={1}
										disabled={!itemsEditable}
										// A cleared box stays empty while typing rather than
										// showing a 0 to type after (ADJ-M03-4); 0 fails the check.
										value={line.qty === 0 ? "" : String(line.qty)}
										onChange={(event) =>
											setLine(line.rowId, { qty: Number(event.target.value) || 0 })
										}
									/>
								)}
							</FormField>
							{!values.combine && itemsEditable && packed && packaging.length > 0 && (
								<FormField
									name={`lines[${index}].packageTypeId`}
									label={t("portal.newOrder.lines.package")}
									className="sm:w-52"
								>
									{(control) => (
										<Select
											{...control}
											value={line.packageTypeId === "" ? STORE_DEFAULT : line.packageTypeId}
											// "Store default" is a choice, so a picked package can be
											// taken back (ADJ-M03-4).
											onValueChange={(next) =>
												setLine(line.rowId, {
													packageTypeId: next === STORE_DEFAULT ? "" : next,
												})
											}
											options={[
												{
													value: STORE_DEFAULT,
													label: t("portal.newOrder.lines.packageOrder"),
												},
												...packageOptions,
											]}
										/>
									)}
								</FormField>
							)}
							<p className="text-[12.5px] tabular-nums text-fg sm:w-24 sm:text-end">
								{money(line.qty * line.unitPriceMinor)}
							</p>
							{itemsEditable && (
								<Button
									type="button"
									variant="ghost"
									className="min-h-12 md:min-h-0"
									onClick={() =>
										form.set(
											"lines",
											values.lines.filter((row) => row.rowId !== line.rowId),
										)
									}
									aria-label={t("portal.newOrder.lines.remove", { name: line.name })}
								>
									<Trash2 aria-hidden className="size-4" />
								</Button>
							)}
						</li>
					))}
				</ul>
				{itemsEditable && packagingQuery.isError && (
					<div role="alert" className="flex flex-wrap items-center gap-2 text-[12.5px]">
						<span className="text-danger-fg">{t("portal.newOrder.packagingFailed")}</span>
						<Button
							type="button"
							variant="secondary"
							size="sm"
							onClick={() => void packagingQuery.refetch()}
						>
							{t("portal.newOrder.retry")}
						</Button>
					</div>
				)}
				{itemsEditable && noPackaging && (
					<p className="text-[12.5px] text-fg-muted">{t("portal.newOrder.noPackaging")}</p>
				)}
				{itemsEditable && packed && !values.combine && packaging.length > 0 && (
					<FormField
						name="packageTypeId"
						label={t("portal.newOrder.orderPackage")}
						hint={t("portal.newOrder.orderPackageHint")}
					>
						{(control) => (
							<Select
								{...control}
								value={values.packageTypeId === "" ? STORE_DEFAULT : values.packageTypeId}
								onValueChange={(next) =>
									form.set("packageTypeId", next === STORE_DEFAULT ? "" : next)
								}
								options={[
									{ value: STORE_DEFAULT, label: t("portal.newOrder.lines.packageDefault") },
									...packageOptions,
								]}
							/>
						)}
					</FormField>
				)}
				{itemsEditable && packed && packaging.length > 0 && (
					<Switch
						label={t("portal.newOrder.combine")}
						checked={values.combine}
						onCheckedChange={(checked) => form.set("combine", checked)}
					/>
				)}
				{itemsEditable && packed && values.combine && (
					<FormField
						name="combinedPackageTypeId"
						label={t("portal.newOrder.combinedPackage")}
						hint={t("portal.newOrder.combinedHint")}
						required
					>
						{(control) => (
							<Select
								{...control}
								value={values.combinedPackageTypeId}
								onValueChange={(next) => form.set("combinedPackageTypeId", next)}
								options={packageOptions}
							/>
						)}
					</FormField>
				)}
			</FormSection>

			<div className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-2 border-t border-border bg-bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
				<dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[12.5px] sm:grid-cols-4">
					<dt className="text-fg-muted">{t("portal.newOrder.totals.subtotal")}</dt>
					<dd className="tabular-nums">{money(totals.goodsMinor)}</dd>
					<dt className="text-fg-muted">{t("portal.newOrder.totals.packaging")}</dt>
					<dd className="tabular-nums">{money(totals.packagingMinor)}</dd>
					<dt className="text-fg-muted">{t("portal.newOrder.totals.delivery")}</dt>
					<dd>{t("portal.newOrder.totals.deliveryLater")}</dd>
					<dt className="font-bold text-fg">{t("portal.newOrder.totals.toCollect")}</dt>
					<dd className="font-bold tabular-nums">{money(totals.toCollectMinor)}</dd>
				</dl>
				<Button type="submit" loading={form.submitting} className="min-h-12 md:min-h-0">
					{t(`portal.newOrder.submit.${mode}`)}
				</Button>
			</div>
		</Form>
	);
}

/**
 * The answer, in the server's numbers: whatever the preview said, these are
 * the totals the order was written with (EP10-S05).
 */
function Confirmation({
	result,
	preview,
	onAnother,
}: {
	result: PortalOrderIntakeResult;
	preview: ReturnType<typeof previewTotals>;
	onAnother: () => void;
}) {
	const { t, locale } = useLocale();
	const o = result.order;
	const money = (amountMinor: number) =>
		formatMoney({ amountMinor, currency: o.order.currency }, locale);
	const recalculated =
		preview.goodsMinor !== o.goodsTotalMinor || preview.packagingMinor !== o.packagingTotalMinor;
	return (
		<section aria-labelledby="order-submitted" className="flex max-w-xl flex-col gap-3 card p-4">
			<h2 id="order-submitted" className="text-[15px] font-bold text-fg">
				{t(result.duplicate ? "portal.newOrder.done.duplicate" : "portal.newOrder.done.title", {
					orderNo: o.order.orderNo,
				})}
			</h2>
			{recalculated && (
				<p role="status" className="text-[12.5px] text-fg-muted">
					{t("portal.newOrder.done.recalculated")}
				</p>
			)}
			<dl className="grid grid-cols-2 gap-1 text-[13px]">
				<dt>{t("portal.newOrder.totals.subtotal")}</dt>
				<dd className="tabular-nums">{money(o.goodsTotalMinor)}</dd>
				<dt>{t("portal.newOrder.totals.packaging")}</dt>
				<dd className="tabular-nums">{money(o.packagingTotalMinor)}</dd>
				<dt>{t("portal.newOrder.totals.total")}</dt>
				<dd className="tabular-nums">{money(o.order.totalMinor)}</dd>
				<dt className="font-bold">{t("portal.newOrder.totals.toCollect")}</dt>
				<dd className="font-bold tabular-nums">
					{money(o.paymentMethod === "cod" ? o.codAmountMinor : 0)}
				</dd>
			</dl>
			<div className="flex flex-wrap gap-2">
				<Link
					to={`/orders/${encodeURIComponent(o.order.orderId)}`}
					className="text-[13px] font-semibold text-brand-ink underline-offset-2 hover:underline"
				>
					{t("portal.newOrder.done.view")}
				</Link>
				<Button type="button" variant="secondary" size="sm" onClick={onAnother}>
					{t("portal.newOrder.done.another")}
				</Button>
			</div>
		</section>
	);
}

export default NewOrderPage;
